import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Spin } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon from "../../components/icons/BackHome/backhome";
import { getBooking } from "../../utils/bookingApi";
import { formatShootAtDisplay } from "../../utils/bookingFormat";
import { useTranslation } from "../../i18n";
import "./booking.css";

function BookingView() {
    const { id } = useParams();
    const { t } = useTranslation();
    const [status, setStatus] = useState("loading");
    const [booking, setBooking] = useState(null);
    const [errorDebug, setErrorDebug] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let active = true;
        setStatus("loading");
        setErrorDebug(null);
        setNotFound(false);
        (async () => {
            try {
                const data = await getBooking(id);
                if (!active) {
                    return;
                }
                if (!data) {
                    const error = new Error("EMPTY_BOOKING");
                    error.status = 404;
                    error.debug = { stage: "empty", message: "接口返回成功但 booking 为空" };
                    throw error;
                }
                setBooking(data);
                setStatus("ok");
            } catch (error) {
                if (!active) {
                    return;
                }
                const debug = {
                    id,
                    status: error?.status,
                    message: error?.message || String(error),
                    ...(error?.debug || {}),
                };
                // 仅 404（含成功但 booking 为空）视为「确实不存在」；其余一律按网络问题处理。
                setNotFound(error?.status === 404);
                setErrorDebug(debug);
                setStatus("error");
            }
        })();
        return () => {
            active = false;
        };
    }, [id]);

    useEffect(() => {
        const brand = "HEI CREATIVE";
        if (status === "ok" && booking) {
            document.title = `${booking.title} · ${brand}`;
        } else if (status === "error") {
            const errorTitle = notFound
                ? t("booking.view.notFoundTitle")
                : t("booking.view.networkErrorTitle");
            document.title = `${errorTitle} · ${brand}`;
        } else {
            document.title = `${t("booking.view.loading")} · ${brand}`;
        }
    }, [status, booking, notFound, t]);

    const splitLines = (value) =>
        String(value || "")
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);
    const contentLines = booking ? splitLines(booking.content) : [];
    const notesLines = booking ? splitLines(booking.notes) : [];

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon />
            <div className="booking-view">
                {status === "loading" && (
                    <div className="booking-view__center">
                        <Spin size="large" />
                    </div>
                )}

                {status === "error" && (
                    <div className="booking-view__center booking-view__error">
                        <div className="booking-view__error-title">
                            {notFound
                                ? t("booking.view.notFoundTitle")
                                : t("booking.view.networkErrorTitle")}
                        </div>
                        <div className="booking-view__error-text">
                            {notFound
                                ? t("booking.view.notFoundText")
                                : t("booking.view.networkErrorText")}
                        </div>
                        {!notFound && (
                            <div
                                className="booking-view__error-hint"
                                style={{ marginTop: 8, fontSize: 13, color: "#8a6d3b" }}
                            >
                                {t("booking.view.troubleshootHint")}
                            </div>
                        )}
                        <div
                            className="booking-view__error-contact"
                            style={{ marginTop: 12, fontSize: 14 }}
                        >
                            {t("booking.view.contactPrompt")}{" "}
                            <Link to="/contact">{t("booking.view.contactLink")}</Link>
                        </div>
                        {errorDebug && (
                            <details style={{ marginTop: 16, maxWidth: "92vw", textAlign: "left" }}>
                                <summary
                                    style={{
                                        cursor: "pointer",
                                        fontSize: 12,
                                        color: "#999",
                                        userSelect: "none",
                                    }}
                                >
                                    {t("booking.view.debugToggle")}
                                </summary>
                                <pre
                                    style={{
                                        marginTop: 8,
                                        padding: 12,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-all",
                                        fontSize: 12,
                                        lineHeight: 1.5,
                                        color: "#b00020",
                                        background: "#fff5f5",
                                        border: "1px solid #ffd6d6",
                                        borderRadius: 8,
                                    }}
                                >
                                    {JSON.stringify(errorDebug, null, 2)}
                                </pre>
                            </details>
                        )}
                    </div>
                )}

                {status === "ok" && booking && (
                    <div className="booking-view__card">
                        <div className="booking-view__brand">HEI CREATIVE</div>
                        <div className="booking-view__doc-title">
                            {t("booking.poster.title")}
                            <span className="booking-view__doc-title-en">{t("booking.poster.titleEn")}</span>
                        </div>
                        <h1 className="booking-view__project">{booking.title}</h1>

                        <div className="booking-view__rows">
                            <div className="booking-view__row">
                                <div className="booking-view__label">{t("booking.poster.time")}</div>
                                <div className="booking-view__value">
                                    {formatShootAtDisplay(booking.shootAt)}
                                    <span className="booking-view__local-time">
                                        {t("booking.poster.localTime")}
                                    </span>
                                </div>
                            </div>
                            {booking.duration ? (
                                <div className="booking-view__row">
                                    <div className="booking-view__label">{t("booking.poster.duration")}</div>
                                    <div className="booking-view__value">{booking.duration}</div>
                                </div>
                            ) : null}
                            <div className="booking-view__row">
                                <div className="booking-view__label">{t("booking.poster.location")}</div>
                                <div className="booking-view__value">{booking.location}</div>
                            </div>
                            {contentLines.length === 1 ? (
                                <div className="booking-view__row">
                                    <div className="booking-view__label">{t("booking.poster.content")}</div>
                                    <div className="booking-view__value">{contentLines[0]}</div>
                                </div>
                            ) : contentLines.length > 1 ? (
                                <div className="booking-view__row booking-view__row--block">
                                    <div className="booking-view__label">{t("booking.poster.content")}</div>
                                    <div className="booking-view__content">
                                        {contentLines.map((line, index) => (
                                            <div key={`line-${index}`} className="booking-view__content-line">
                                                {line}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {notesLines.length > 0 ? (
                                <div className="booking-view__row booking-view__row--block">
                                    <div className="booking-view__label">{t("booking.poster.notes")}</div>
                                    <div className="booking-view__content">
                                        {notesLines.map((line, index) => (
                                            <div key={`note-${index}`} className="booking-view__content-line">
                                                {line}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="booking-view__website">HEICREATIVE.CO.UK</div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}

export default BookingView;
